import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { routeInspectionSheet } from "@/setup/routes/inspection.routes";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { alpha, Box, Chip, Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { InspectionDetailPanel } from "../../components/InspectionDetailPanel";
import type { Inspection } from "../../models/Inspection";
import { useInspectionsOptionsFunctions } from "../hooks/useInspectionsOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { DeleteInspectionModal } from "./DeleteInspectionModal";
import { NewInspectionModal } from "./NewInspectionModal";
import {
	KeyboardDoubleArrowDown,
	KeyboardDoubleArrowUp,
} from "@mui/icons-material";

type Props = {
	inspections: Inspection[];
	isLoading: boolean;
	shiftId: number | null;
};

export const InspectionsTable = ({
	inspections,
	isLoading,
	shiftId,
}: Props) => {
	const navigate = useNavigate();

	const [showModal, setShowModal] = useState<"create" | "delete" | "">("");
	const [inspectionToDelete, setInspectionToDelete] =
		useState<Inspection | null>(null);
	const [notificationMessage, setNotificationMessage] = useState("");
	const [error, setError] = useState("");
	const [expandAll, setExpandAll] = useState(true);

	const gridApi = useRef<GridApi<Inspection> | null>(null);
	const expandedInspections = useRef(new Set<string>());
	const isApplyingExpansion = useRef(false);

	const applyExpansion = () => {
		isApplyingExpansion.current = true;

		gridApi.current?.forEachNode((node) => {
			if (node.id === undefined) return;

			if (expandAll) expandedInspections.current.add(node.id);

			node.setExpanded(expandedInspections.current.has(node.id));
		});

		isApplyingExpansion.current = false;
	};

	const syncExpandAll = () => {
		let inspectionCount = 0;
		let expandedCount = 0;

		gridApi.current?.forEachNode((node) => {
			inspectionCount += 1;
			if (node.expanded) expandedCount += 1;
		});

		if (inspectionCount === 0) return;

		if (expandedCount === inspectionCount && !expandAll) setExpandAll(true);
		else if (expandedCount === 0 && expandAll) setExpandAll(false);
	};

	const openInspection = (inspection: Inspection) =>
		navigate(`${routeInspectionSheet}/${inspection.InspectionId}`);

	const deleteInspection = (inspection: Inspection) => {
		setInspectionToDelete(inspection);
		setShowModal("delete");
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useInspectionsOptionsFunctions({
		handleNewInspection: () => setShowModal("create"),
		openInspection,
		deleteInspection,
	});
	const { columnDefs } = useTableData({ openInspection, deleteInspection });
	const { defaultColDef } = useGridDefinitions({});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "inspectionsContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[inspections],
	);

	useEffect(() => {
		if (!expandAll) expandedInspections.current.clear();

		applyExpansion();
	}, [expandAll]);

	useEffect(() => {
		registerConfig({
			id: "inspectionsContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper
						elevation={2}
						sx={{
							height: "64vh",
							width: "100%",
							display: "flex",
							flexDirection: "column",
							"& .ag-details-row": { padding: 0 },
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								gap: 1,
								px: 1.5,
								py: 1,
								borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
							}}
						>
							<Typography
								variant="body2"
								color="text.secondary"
								fontWeight={600}
							>
								{inspections.length === 1
									? "1 inspection"
									: `${inspections.length} inspections`}
							</Typography>
							<Chip
								clickable
								aria-pressed={expandAll}
								icon={
									expandAll ? (
										<KeyboardDoubleArrowUp sx={{ fontSize: 16 }} />
									) : (
										<KeyboardDoubleArrowDown sx={{ fontSize: 16 }} />
									)
								}
								label={expandAll ? "Collapse all" : "Expand all"}
								onClick={() => setExpandAll(!expandAll)}
								sx={{
									backgroundColor: expandAll
										? (theme) => alpha(theme.palette.primary.main, 0.12)
										: "background.paper",
									border: 1,
									borderColor: expandAll
										? (theme) => alpha(theme.palette.primary.main, 0.5)
										: "divider",
									borderRadius: 1,
									color: expandAll ? "primary.main" : "text.secondary",
									fontSize: "0.8125rem",
									fontWeight: expandAll ? 600 : 500,
									height: 30,
									px: 0.5,
									transition:
										"background-color 120ms, border-color 120ms, color 120ms",
									"& .MuiChip-icon": { color: "inherit", ml: 0.75, mr: -0.25 },
									"&:hover": {
										backgroundColor: expandAll
											? (theme) => alpha(theme.palette.primary.main, 0.18)
											: "action.hover",
										borderColor: expandAll ? "primary.main" : "text.disabled",
										color: expandAll ? "primary.main" : "text.primary",
									},
								}}
							/>
						</Box>
						<Box sx={{ flex: 1, minHeight: 0 }}>
							<AgGridReact<Inspection>
								loading={isLoading}
								rowData={inspections}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								masterDetail
								detailCellRenderer={InspectionDetailPanel}
								detailRowAutoHeight
								onGridReady={(event) => {
									gridApi.current = event.api;
									applyExpansion();
								}}
								onRowDataUpdated={applyExpansion}
								onRowGroupOpened={(event) => {
									if (event.node.id === undefined) return;

									if (event.node.expanded)
										expandedInspections.current.add(event.node.id);
									else expandedInspections.current.delete(event.node.id);

									if (!isApplyingExpansion.current) syncExpandAll();
								}}
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={(event) =>
									event.data ? openInspection(event.data) : null
								}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.InspectionId}`}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Box>
					</Paper>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<FooterButton
						label="INSPECTION"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(show) => setShowModal(show ? "create" : "")}
						sizes={{ md: 3 }}
					/>
				</Grid2>
			</Grid2>
			<NewInspectionModal
				modal={showModal === "create" ? "create" : ""}
				shiftId={shiftId}
				onHide={() => setShowModal("")}
			/>
			<DeleteInspectionModal
				inspection={inspectionToDelete}
				open={showModal === "delete"}
				onHide={() => setShowModal("")}
			/>
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
